import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Users, Search, IdCard, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import ComplianceRiskAlert from '../../components/inservice/ComplianceRiskAlert';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ROLE_COLOR = {
  super_admin: '#4338CA', practice_admin: '#059669', physician: '#1D4ED8',
  nurse: '#0E7490', aide: '#D97706', care_coordinator: '#7C3AED',
  billing_specialist: '#BE185D', csm_agent: '#475569',
  asset_manager: '#EA580C', patient: '#64748B',
};

export default function HRListPage() {
  const [list, setList] = useState([]);
  const [riskMap, setRiskMap] = useState({});
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/hr/employees`, { headers: authHeader() })
      .then(r => setList(r.data?.employees || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    // Build a quick lookup map of at-risk aide ids → severity for inline badges.
    axios.get(`${API_URL}/api/inservice/compliance/aides-at-risk`, { headers: authHeader() })
      .then(r => {
        const m = {};
        (r.data?.aides || []).forEach(a => { m[a.user_id] = a; });
        setRiskMap(m);
      })
      .catch(() => {});
  }, []);

  const filtered = q
    ? list.filter(e => (e.name + ' ' + e.email + ' ' + (e.job_title || '') + ' ' + (e.department || ''))
        .toLowerCase().includes(q.toLowerCase()))
    : list;

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="hr-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <Users className="w-6 h-6 text-[#0E7490]" /> HR & ID Cards
          </h1>
          <p className="text-sm text-[#64748B] mt-1">{list.length} employees · click any row for profile + printable ID badge.</p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input placeholder="Search name, title, dept…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="hr-search" />
        </div>
      </div>

      <div className="mb-4"><ComplianceRiskAlert compact /></div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-[#E2E8F0] border-dashed">
          <Users className="w-10 h-10 mx-auto text-[#94A3B8] mb-2" />
          <p className="text-sm text-[#64748B]">No employees match.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(e => {
            const color = ROLE_COLOR[e.role] || '#64748B';
            return (
              <Link key={e.user_id} to={`/hr/${e.user_id}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] bg-white hover:shadow-sm transition-shadow"
                    data-testid={`hr-row-${e.user_id}`}>
                <div className="w-1 self-stretch rounded-l-md" style={{ background: color, minHeight: 56 }} />
                <div className="w-12 h-12 rounded-full bg-[#F1F5F9] flex items-center justify-center text-sm font-bold text-[#64748B] overflow-hidden shrink-0">
                  {(e.name || '?').split(/\s+/).slice(0, 2).map(s => s[0]).join('').toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0A2342] truncate">{e.name}</div>
                  <div className="text-xs text-[#64748B] truncate">{e.work_email || e.email}</div>
                  <div className="text-[11px] text-[#94A3B8] truncate">
                    {e.job_title || e.role?.replace(/_/g, ' ')}
                    {e.department ? ` · ${e.department}` : ''}
                    {e.employment_type ? ` · ${e.employment_type.replace('_', ' ')}` : ''}
                  </div>
                </div>
                {!e.onboarding_complete && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] mr-1">Onboarding</Badge>
                )}
                {riskMap[e.user_id] && (
                  <Badge
                    title={`In-service: ${riskMap[e.user_id].completed}h done, expected ${riskMap[e.user_id].expected_by_now}h. Gap ${riskMap[e.user_id].gap}h.`}
                    className={`text-[10px] mr-1 inline-flex items-center gap-1 ${
                      riskMap[e.user_id].severity === 'red'
                        ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]'
                        : 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]'
                    }`}
                    data-testid={`risk-badge-${e.user_id}`}>
                    <AlertTriangle className="w-2.5 h-2.5" />
                    −{riskMap[e.user_id].gap}h
                  </Badge>
                )}
                <Badge style={{ background: color, color: 'white', border: 0 }} className="text-[10px] uppercase whitespace-nowrap">
                  {e.role?.replace('_', ' ')}
                </Badge>
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
